import { PlaidItem } from '@/types/PlaidItem'
import { defineStore } from 'pinia'
import { useMessagesStore } from './messages'
import { useAuthStore } from './auth'
import { useLoadingStore, LoadingType } from './loading'
import { toRaw } from 'vue'
import { AccountType } from 'plaid'

/**
 * Account types that represent liabilities (debt) rather than assets.
 * These should be subtracted from the total balance.
 */
const LIABILITY_ACCOUNT_TYPES: AccountType[] = [
  AccountType.Credit,
  AccountType.Loan,
]

/**
 * Checks if an account type represents a liability (debt).
 * @param accountType - The Plaid account type
 * @returns true if the account is a liability type
 */
const isLiabilityAccountType = (accountType: AccountType): boolean => {
  return LIABILITY_ACCOUNT_TYPES.includes(accountType)
}

type GetItemsOptions = {
  throwOnError?: boolean
}

const getItemsRequests = new Map<string, Promise<void>>()
let latestGetItemsRequestId = 0

const awaitGetItemsRequest = async (
  request: Promise<void>,
  throwOnError = false,
) => {
  try {
    await request
  } catch (error) {
    if (throwOnError) {
      throw error
    }
  }
}

export const usePlaidItemsStore = defineStore('plaidItems', {
  state: () => ({
    items: [] as PlaidItem[],
  }),
  getters: {
    /**
     * Gets the balance for an account, preferring current over available.
     * For liability accounts (credit/loan), only uses current balance (never available, which represents credit limit).
     * @param accountId - The account ID
     * @returns The account balance as a number
     */
    getAccountBalance(): (accountId: string) => number {
      return (accountId: string) => {
        const account = this.getAccountById(accountId)
        if (!account) return 0

        if (account.balances.current != null) {
          return account.balances.current
        }
        return isLiabilityAccountType(account.type)
          ? 0
          : account.balances.available ?? 0
      }
    },
    getItemById: (state) => (itemId: string) => {
      return state.items.find((item) => item.itemId === itemId)
    },
    findAccount: (state) => (itemId: string, accountId: string) => {
      const item = state.items.find((item) => item.itemId === itemId)
      return {
        item,
        account: item?.accounts?.find((acc) => acc.account_id === accountId),
        itemIndex: state.items.findIndex((item) => item.itemId === itemId),
      }
    },
    getBalance(): () => number {
      return () => {
        return this.items.reduce((total: number, item: PlaidItem) => {
          if (item.accounts) {
            const balance = item.accounts.reduce(
              (accountTotal: number, account: any) => {
                if (account.includeInBalance) {
                  const balance = this.getAccountBalance(account.account_id)
                  return isLiabilityAccountType(account.type)
                    ? (accountTotal -= balance)
                    : (accountTotal += balance)
                }
                return accountTotal
              },
              0,
            )
            return total + balance
          }
          return total
        }, 0)
      }
    },
    getAllAccounts: (state) => () => {
      return state.items.reduce(
        (accounts, item) => {
          if (item.accounts) {
            return [...(accounts || []), ...(item.accounts || [])]
          }
          return accounts
        },
        [] as PlaidItem['accounts'],
      )
    },
    getAccountById: (state) => (accountId: string) => {
      const allAccounts = state.items.reduce(
        (accounts, item) => {
          if (item.accounts) {
            return [...(accounts || []), ...(item.accounts || [])]
          }
          return accounts
        },
        [] as PlaidItem['accounts'],
      )
      return allAccounts?.find((acc) => acc.account_id === accountId) || null
    },
    getItemByAccountId: (state) => (accountId: string) => {
      return state.items.find((item) => {
        return item.accounts?.find(
          (account) => account.account_id === accountId,
        )
      })
    },
    getAccountNumber(): (accountId: string) => string | null {
      return (accountId: string) => {
        const account = this.getAccountById(accountId)
        return account?.mask ? account.mask.slice(-4) : null
      }
    },
    getInstitutionName(): (accountId: string) => string | null {
      return (accountId: string) => {
        const account = this.getAccountById(accountId)
        if (!account) return null

        const item = this.getItemByAccountId(accountId)
        return item?.institution?.name || 'Unknown Institution'
      }
    },
    getAccountDetailsForDisplay(): (accountId: string) => string | null {
      return (accountId: string) => {
        const account = this.getAccountById(accountId)
        if (!account) return null

        const accountNumber = this.getAccountNumber(accountId)
        const accountType = account.type || 'account'

        return `${
          accountType.charAt(0).toUpperCase() + accountType.slice(1)
        } •••• ${accountNumber}`
      }
    },
    isAccountIncludedInBalance(): (accountId: string) => boolean {
      return (accountId: string) => {
        const account = this.getAccountById(accountId)
        return account?.includeInBalance || false
      }
    },
    /**
     * Checks if an account is a liability type (credit card, loan, etc.)
     * @param accountId - The account ID
     * @returns true if the account is a liability type
     */
    isAccountLiability(): (accountId: string) => boolean {
      return (accountId: string) => {
        const account = this.getAccountById(accountId)
        return account ? isLiabilityAccountType(account.type) : false
      }
    },
    getFilterableAccounts: (state) => () => {
      return state.items.reduce((accounts, item) => {
        if (item.accounts) {
          const filterableAccounts = item.accounts.filter(
            (account) => account.showTransactions !== false,
          )
          return [...accounts, ...filterableAccounts]
        }
        return accounts
      }, [] as any[])
    },
    getAccountsIncludedInBalance: (state) => () => {
      return state.items.reduce((accounts, item) => {
        if (item.accounts) {
          const includedAccounts = item.accounts.filter(
            (account) => account.includeInBalance === true,
          )
          return [...accounts, ...includedAccounts]
        }
        return accounts
      }, [] as any[])
    },
    getAccountDisplayName(): (accountId: string) => string | null {
      return (accountId: string) => {
        const institutionName = this.getInstitutionName(accountId)
        const last4 = this.getAccountNumber(accountId)

        if (!institutionName || !last4) return null

        return `${institutionName} - ${last4}`
      }
    },
  },
  actions: {
    async loadFromDeviceStorage() {
      const storedAccounts = await this.ionicStorage.get('accounts')
      if (storedAccounts) {
        this.items = storedAccounts
      }
    },
    async getItems(sync = false, options: GetItemsOptions = {}) {
      const requestKey = sync ? 'sync' : 'cached'
      const syncRequest = getItemsRequests.get('sync')
      if (!sync && syncRequest) {
        return awaitGetItemsRequest(syncRequest, options.throwOnError)
      }

      const existingRequest = getItemsRequests.get(requestKey)
      if (existingRequest) {
        return awaitGetItemsRequest(existingRequest, options.throwOnError)
      }

      const loadingStore = useLoadingStore()
      const requestId = ++latestGetItemsRequestId
      const request = (async () => {
        loadingStore.startLoading(LoadingType.FetchAccounts)
        try {
          const { data } = await this.axios.get(`/items`, {
            params: { sync },
          })
          const messagesStore = useMessagesStore()
          const authStore = useAuthStore()

          if (data.updateRequired) {
            messagesStore.messages.push({
              message: 'Your bank account needs updated credentials.',
              duration: 10000,
            })
          }

          if (data.items.length == 0 && authStore.onboardingComplete) {
            messagesStore.messages.push({
              message: `You don't have any accounts linked.`,
              duration: 10000,
            })
          }

          if (requestId === latestGetItemsRequestId) {
            this.items = data.items
            this.ionicStorage.set('accounts', toRaw(this.items))
          }
        } catch (error) {
          console.error(error)
          throw error
        } finally {
          loadingStore.finishLoading(LoadingType.FetchAccounts)
        }
      })()

      getItemsRequests.set(requestKey, request)
      try {
        await awaitGetItemsRequest(request, options.throwOnError)
      } finally {
        getItemsRequests.delete(requestKey)
      }
    },
    async syncTransactionsForItem(itemId: string) {
      try {
        await this.axios.get(`/items/${itemId}/syncTransactions`)
      } catch (error) {
        console.error(error)
      }
    },

    async getAccountsForItem(itemId: string) {
      try {
        const response = await this.axios.get(`/items/${itemId}/getAccounts`)
        const itemIndex = this.items.findIndex((item) => item.itemId === itemId)
        if (itemIndex !== -1) {
          this.items[itemIndex].accounts = response.data
        }
      } catch (error) {
        console.error(error)
      }
    },
    async removeItem(itemId: string) {
      try {
        await this.axios.delete(`/items/${itemId}`)
      } catch (error) {
        console.error(error)
      }
    },
    async updateAccountSettings(
      itemId: string,
      accountId: string,
      settings: {
        includeInBalance?: boolean
        showTransactions?: boolean
      },
    ) {
      try {
        await this.axios.post(`/items/${itemId}/${accountId}`, settings)

        const { item, account, itemIndex } = this.findAccount(itemId, accountId)
        if (item?.accounts && account) {
          const accountIndex = item.accounts.indexOf(account)
          this.items[itemIndex].accounts![accountIndex] = {
            ...account,
            ...settings,
          }
        }
      } catch (error) {
        console.error(error)
      }
    },
  },
})
